import { usePlayer, useGame } from "@empirica/core/player/classic/react";
import React, { useState, useEffect } from "react";
import { Alert } from "./Alert.jsx";
import { getCompensationConfig } from "../utils/conditionUtils.js";

export function LobbyWaiting({ onTimeout }) {
  const player = usePlayer();
  const game = useGame();
  const treatment = game?.get("treatment") || {};
  const treatmentType = treatment.treatmentType || 
    ((treatment.name || "").toLowerCase().includes("synthetic") ? "Synthetic Peers" : undefined);
  const isSyntheticPeers = treatmentType === "Synthetic Peers";
  const compensation = getCompensationConfig(treatment);
  
  // For synthetic peers, wait only 5 seconds; otherwise 10 minutes
  const [timeLeft, setTimeLeft] = useState(isSyntheticPeers ? 5 : 600);
  const [hasTimedOut, setHasTimedOut] = useState(false);

  useEffect(() => {
    if (timeLeft <= 0 || hasTimedOut) return;

    const timer = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 1) {
          setHasTimedOut(true);
          // For synthetic peers, auto-start the game after 5 seconds
          if (isSyntheticPeers) {
            // Trigger game start by setting a flag that server will detect
            player.set("syntheticLobbyComplete", true);
            player.set("syntheticLobbyCompleteAt", Date.now());
            return 0;
          } else {
            // Set a flag on the player to indicate they timed out
            player.set("lobbyTimeout", true);
            player.set("lobbyTimeoutAt", Date.now());
            // Trigger server event for lobby timeout - using set for now
            // Call the timeout callback if provided
            if (onTimeout) {
              onTimeout();
            }
            return 0;
          }
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft, hasTimedOut, player, onTimeout, isSyntheticPeers]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getUrgencyColor = () => {
    if (timeLeft > 60) return "text-gray-600";
    if (timeLeft > 30) return "text-yellow-600";
    return "text-red-600";
  };

  if (hasTimedOut) {
    return (
      <div className="text-center space-y-6">
        <div className="max-w-2xl mx-auto">
          <Alert title="Time Limit Reached">
            <p className="text-lg">
              Unfortunately, we weren't able to find enough other participants to start your game within the 10-minute time limit.
            </p>
            <p className="mt-2">
              You will still receive the {compensation.basePaymentAmountText} base payment for your time. Please proceed to the exit survey to complete the study.
            </p>
          </Alert>

          <div className="mt-6">
            <button
              onClick={() => {
                // Prefer a per-player exit so the game/lobby isn't closed for others
                if (typeof player.exit === "function") {
                  try {
                    player.exit("lobby_timeout");
                    return;
                  } catch (_) {
                    // Fall through to readyToExit if exit is not available
                  }
                }
                player.set("readyToExit", true);
              }}
              className="bg-empirica-500 hover:bg-empirica-600 text-white px-6 py-3 rounded-md font-medium"
            >
              Continue to Exit Survey
            </button>
          </div>
        </div>
      </div>
    );
  }

  // For synthetic peers, show a different message
  if (isSyntheticPeers) {
    return (
      <div className="text-center space-y-6">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Matching You with Other Participants
          </h2>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
            <p className="text-lg text-blue-800 mb-4">
              Please wait while we match you with other participants and prepare the game.
            </p>

            <div className="text-sm text-blue-700 space-y-2">
              <p><strong>Time remaining:</strong> <span className={`font-mono text-lg ${getUrgencyColor()}`}>{formatTime(timeLeft)}</span></p>
              <p>The game will start shortly...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="text-center space-y-6">
      <div className="max-w-2xl mx-auto">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          Waiting for Other Participants
        </h2>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
          <p className="text-lg text-blue-800 mb-4">
            We're looking for other participants to join your game. Please wait while we find suitable players.
          </p>

          <div className="text-sm text-blue-700 space-y-2">
            <p><strong>Time remaining:</strong> <span className={`font-mono text-lg ${getUrgencyColor()}`}>{formatTime(timeLeft)}</span></p>
            <p>We're waiting for enough participants to join your specific game. If we can't find enough participants within <strong>10 minutes</strong>, you'll be redirected to complete the study with the {compensation.basePaymentAmountText} base payment.</p>
            <p>Your responses will still be valuable to our research!</p>
          </div>
        </div>

        <div className="text-sm text-gray-500">
          <p>Please keep this window open and don't refresh the page.</p>
        </div>
      </div>
    </div>
  );
}
